import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, evDate } from "@/lib/api";
import { themeFor } from "@/lib/themes";
import { Confetti, Stamp, Wordmark, Asterisk, Heart } from "@/components/Motifs";
import { useGuest } from "@/lib/GuestContext";

const STATUS = [["attending", "Count me in"], ["declined", "Sadly, no"], ["undecided", "Not sure yet"]];
const eventWhen = (ev) => [evDate(ev.date).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" }), ev.start_time].filter(Boolean).join(" · ");

export default function Invitation() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [opened, setOpened] = useState(false);
  const [err, setErr] = useState("");
  const [responses, setResponses] = useState({});
  const [specialRequests, setSpecialRequests] = useState("");
  const [arrival, setArrival] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [burst, setBurst] = useState(0);
  const { refresh } = useGuest();

  useEffect(() => {
    api.get(`/public/invite/${token}`).then((r) => {
      setData(r.data);
      localStorage.setItem("nigi_invite_token", token);
      refresh();
      const map = {};
      (r.data.rsvp?.responses || []).forEach((rr) => {
        map[`${rr.member_id}_${rr.event_slug}`] = { status: rr.status, dietary: rr.dietary || "", allergies: rr.allergies || "" };
      });
      setResponses(map);
      setSpecialRequests(r.data.rsvp?.special_requests || "");
      setArrival(r.data.rsvp?.arrival_details || "");
    }).catch(() => {
      if (localStorage.getItem("nigi_invite_token") === token) localStorage.removeItem("nigi_invite_token");
      setErr("Hmm, this invitation link doesn't look right.");
    });
  }, [token, refresh]);

  if (err) return <ErrorScreen msg={err} />;
  if (!data) return <div className="min-h-screen bg-[#F7C9D0] flex items-center justify-center font-hand text-2xl">Licking the envelope…</div>;
  if (!opened) return <EnvelopeIntro family={data.family} onOpen={() => setOpened(true)} />;

  const setResp = (mid, slug, patch) => {
    const k = `${mid}_${slug}`;
    setResponses((prev) => ({ ...prev, [k]: { ...(prev[k] || { status: "", dietary: "", allergies: "" }), ...patch } }));
  };

  const submit = async () => {
    const payload = { token, special_requests: specialRequests, arrival_details: arrival, responses: [] };
    data.family.members.forEach((m) => data.events.filter((ev) => m.event_slugs.includes(ev.slug)).forEach((ev) => {
      const r = responses[`${m.id}_${ev.slug}`];
      if (r?.status) payload.responses.push({ member_id: m.id, event_slug: ev.slug, status: r.status, dietary: r.dietary || "", allergies: r.allergies || "" });
    }));
    if (!payload.responses.length) { toast.error("Pick an answer for at least one celebration first."); return; }
    setSubmitting(true);
    try {
      await api.post("/public/invite/rsvp", payload);
      toast.success("Your RSVP is saved. Thank you!");
      setDone(true); setBurst((b) => b + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save RSVP");
    } finally { setSubmitting(false); }
  };

  const joining = Object.values(responses).filter((r) => r.status === "attending").length;

  return (
    <div data-testid="invitation-page" className="min-h-screen bg-[#FFF9F0]">
      <Confetti fire={burst} />
      <header className="border-b-2 border-[#252122] bg-[#FFF9F0]">
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" aria-label="NiGi home"><Wordmark className="text-2xl" /></Link>
          <Link to="/celebrations" className="kicker link-sweep" data-testid="invite-explore">Explore the celebrations</Link>
        </div>
      </header>

      {done ? (
        <section data-testid="rsvp-success" className="bg-[#A9C79D] min-h-[calc(100vh-66px)] flex items-center">
          <div className="max-w-4xl mx-auto px-5 py-16 md:py-20 text-center relative w-full">
            <Heart className="mx-auto text-[#A62D58]" size={56} />
            <h1 className="font-display italic text-5xl md:text-7xl mt-4">You're on the list!</h1>
            <p className="font-hand text-2xl md:text-3xl mt-4">{joining > 0 ? `${joining} RSVP${joining > 1 ? "s" : ""} marked "count me in". Start practising those dance moves.` : "Thank you for letting us know. We'll miss you, and we'll save you some mithai."}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mt-8">
              <button onClick={() => setDone(false)} data-testid="rsvp-edit" className="btn-pop bg-[#FFF9F0] rounded-full px-6 py-3.5 kicker w-full sm:w-auto max-w-xs">Edit my RSVP</button>
              <Link to="/dress-code" data-testid="rsvp-plan-outfits" className="btn-pop bg-[#FF6657] rounded-full px-6 py-3.5 kicker w-full sm:w-auto max-w-xs text-center">Plan my outfits</Link>
              <Link to="/story" data-testid="rsvp-view-story" className="btn-pop bg-[#F7B733] rounded-full px-6 py-3.5 kicker w-full sm:w-auto max-w-xs text-center">View our story</Link>
            </div>
          </div>
        </section>
      ) : (
        <>
          <section className="bg-[#F7C9D0] border-b-2 border-[#252122] relative overflow-hidden">
            <div className="max-w-4xl mx-auto px-5 py-14 md:py-20 relative">
              <div className="kicker text-[#A62D58]">11:11 | The art of timing</div>
              <h1 data-testid="invite-family" className="font-display text-5xl md:text-7xl leading-[0.9] mt-4">Dearest<br /><span className="italic text-[#A62D58]">{data.family.family_name},</span></h1>
              <p className="font-display text-2xl md:text-3xl mt-6 max-w-xl leading-snug">It's happening!! And honestly, it wouldn't be the same without you.</p>
              <p className="font-hand text-xl mt-4 max-w-xl">One answer per person, per celebration. Takes about a minute (less than our first meeting).</p>
              <Stamp className="absolute right-4 top-6 w-24 h-24 md:w-36 md:h-36 hidden sm:block" />
              <div className="mt-10 grid sm:grid-cols-2 gap-3" data-testid="invite-summary">
                {data.events.map((ev) => {
                  const t = themeFor(ev.slug);
                  const names = data.family.members.filter((m) => m.event_slugs.includes(ev.slug)).map((m) => m.name);
                  return (
                    <Link key={ev.slug} to={`/celebrations/${ev.slug}`} data-testid={`invite-summary-${ev.slug}`} className="btn-pop block px-5 py-4" style={{ background: t.bg, color: t.fg }}>
                      <div className="kicker opacity-80" data-testid={`invite-summary-when-${ev.slug}`}>{eventWhen(ev)}</div>
                      <div className={`text-2xl mt-1 ${t.titleClass}`}>{ev.name}</div>
                      <div className="text-sm font-semibold mt-1 opacity-90">{ev.venue}</div>
                      <div className="font-hand text-lg mt-1 opacity-90">for {names.join(", ")}</div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          <div className="max-w-4xl mx-auto px-5 py-12 md:py-16 space-y-10">
            {data.events.length === 0 && (
              <div data-testid="no-events" className="border-2 border-[#252122] bg-[#F7B733] p-8 text-center">
                <div className="font-display italic text-3xl">Your celebrations are being assigned.</div>
                <p className="font-hand text-xl mt-2">Check back soon, or ping the family WhatsApp group.</p>
              </div>
            )}
            {data.events.length > 0 && data.family.members.map((m) => (
              <div key={m.id} data-testid={`member-block-${m.id}`} className="border-2 border-[#252122] bg-[#FFF9F0] shadow-[6px_6px_0_#252122]">
                <div className="px-5 md:px-7 py-4 border-b-2 border-[#252122] flex items-center gap-3 bg-[#252122] text-[#FFF9F0]">
                  <Asterisk size={14} className="text-[#F7B733]" />
                  <h3 className="font-display text-2xl md:text-3xl">{m.name}</h3>
                  {m.is_child && <span className="kicker text-[#F7B733] ml-auto">Little guest</span>}
                </div>
                <div className="divide-y-2 divide-[#252122]/10">
                  {data.events.filter((ev) => m.event_slugs.includes(ev.slug)).map((ev) => <EventRow key={ev.slug} m={m} ev={ev} r={responses[`${m.id}_${ev.slug}`] || {}} setResp={setResp} />)}
                </div>
              </div>
            ))}

            {data.events.length > 0 && (<>
            <div>
              <NoteBox label="Special requests" testid="special-requests" value={specialRequests} onChange={setSpecialRequests} placeholder="Allergies, accessibility, song requests (no promises)" />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-4 pt-4">
              <div className="font-hand text-xl text-[#252122]/70">Please RSVP by {data.deadline}</div>
              <button data-testid="rsvp-submit" onClick={submit} disabled={submitting} className="btn-pop bg-[#FF6657] text-[#252122] rounded-full px-10 py-4 kicker disabled:opacity-60">
                {submitting ? "Sending…" : "Send my RSVP"}
              </button>
            </div>
            </>)}
          </div>
        </>
      )}
    </div>
  );
}

function EventRow({ m, ev, r, setResp }) {
  const t = themeFor(ev.slug);
  return (
    <div className="px-5 md:px-7 py-5 grid md:grid-cols-12 gap-4 items-center">
      <div className="md:col-span-5 flex items-center gap-3">
        <span className="w-3 h-14 shrink-0" style={{ background: t.bg === "#FBF5EA" ? t.accent : t.bg }} />
        <div>
          <div className={`text-2xl leading-tight ${t.titleClass}`}>{ev.name}</div>
          <div className="kicker opacity-70 mt-1" data-testid={`rsvp-when-${m.id}-${ev.slug}`}>{eventWhen(ev)}</div>
          <div className="text-sm font-semibold opacity-70 mt-0.5">{ev.venue}</div>
        </div>
      </div>
      <div className="md:col-span-7 flex gap-2 flex-wrap md:justify-end">
        {STATUS.map(([s, label]) => (
          <button key={s} data-testid={`rsvp-${m.id}-${ev.slug}-${s}`} onClick={() => setResp(m.id, ev.slug, { status: s })}
            className={`rounded-full px-4 py-2.5 text-xs font-bold border-2 transition-colors ${r.status === s ? "bg-[#252122] text-[#FFF9F0] border-[#252122]" : "border-[#252122]/25 hover:border-[#252122]"}`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function NoteBox({ label, testid, value, onChange, placeholder }) {
  return (
    <label className="block border-2 border-[#252122] p-5 bg-[#FFF9F0]">
      <span className="kicker text-[#A62D58]">{label}</span>
      <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-transparent outline-none min-h-[90px] mt-2 font-hand text-xl placeholder:text-[#252122]/35" />
    </label>
  );
}

function EnvelopeIntro({ family, onOpen }) {
  const [open, setOpen] = useState(false);
  const click = () => { setOpen(true); setTimeout(onOpen, 1500); };
  return (
    <div className="min-h-screen bg-[#F7C9D0] grain flex flex-col items-center justify-center p-6 overflow-hidden" data-testid="envelope-screen">
      <div className="kicker text-[#A62D58] mb-3">Special delivery</div>
      <p className="font-hand text-2xl md:text-3xl text-center mb-10">psst... this one's for you</p>
      <div className={`envelope ${open ? "is-open" : ""} relative w-full max-w-md aspect-[7/5] mt-16`}>
        <div className="envelope-letter absolute left-[6%] right-[6%] top-[6%] h-[88%] bg-[#FFF9F0] border-2 border-[#252122] flex flex-col items-center justify-center text-center px-6">
          <div className="kicker text-[#A62D58]">You're invited</div>
          <div className="font-display italic text-4xl mt-2">It's happening!!</div>
          <div className="font-hand text-lg mt-2">Niyati &amp; Girish · 03.12.2026</div>
        </div>
        <div className="absolute inset-0 bg-[#A62D58] border-2 border-[#252122] z-[2]" style={{ clipPath: "polygon(0 0, 50% 55%, 100% 0, 100% 100%, 0 100%)" }} />
        <div className="envelope-flap absolute top-0 inset-x-0 h-[58%] bg-[#8A2149] border-2 border-[#252122]" style={{ clipPath: "polygon(0 0, 100% 0, 50% 100%)" }} />
        <div className="absolute right-4 bottom-4 z-[4] w-16 h-20 bg-[#F7B733] border-[3px] border-dashed border-[#FFF9F0] rotate-6 flex flex-col items-center justify-center text-[#252122]">
          <span className="font-display text-lg leading-none">11:11</span><span className="kicker text-[7px] mt-1">Mumbai</span>
        </div>
        <div className="absolute left-6 bottom-6 z-[4] font-hand text-2xl md:text-3xl text-[#FFF9F0] max-w-[65%] leading-tight">For {family.family_name}</div>
        <div className="seal absolute left-1/2 top-[50%] -translate-x-1/2 -translate-y-1/2 z-[5] w-20 h-20 rounded-full bg-[#44253D] border-4 border-[#C6A46C] flex items-center justify-center font-display italic text-[#C6A46C] text-xl shadow-xl">NiGi</div>
        {!open && <button data-testid="envelope-open" onClick={click} className="absolute inset-0 z-[6] w-full h-full" aria-label="Open your invitation" />}
      </div>
      {!open && <div className="mt-12 kicker text-[#252122]/70 animate-pulse">Tap the envelope to open</div>}
    </div>
  );
}

function ErrorScreen({ msg }) {
  return (
    <div className="min-h-screen bg-[#F7B733] flex items-center justify-center p-8 text-center">
      <div className="max-w-lg">
        <div className="font-display italic text-[7rem] leading-none text-[#A62D58]">Oops.</div>
        <h1 className="font-display text-3xl md:text-4xl mt-4">{msg}</h1>
        <p className="font-hand text-2xl mt-4">Double-check the personal link we sent you, or ping the family WhatsApp group. Someone always knows.</p>
        <Link to="/" data-testid="invite-error-home" className="btn-pop mt-8 inline-flex bg-[#FFF9F0] rounded-full px-7 py-4 kicker">Back to home</Link>
      </div>
    </div>
  );
}
